"use client";

import { useRef, useState, useEffect, type RefObject } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible(threshold = 0.1): [RefObject<HTMLDivElement>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, visible] as [RefObject<HTMLDivElement>, boolean];
}

function Reveal({ children, delay = 0, from = "bottom" }: { children: React.ReactNode; delay?: number; from?: "bottom" | "left" | "right" }) {
  const [ref, v] = useVisible();
  const hidden: Record<string, string> = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text, light = false }: { text: string; light?: boolean }) {
  return (
    <span className={`block text-[11px] font-bold tracking-[0.15em] uppercase mb-3.5 ${light ? "text-blue-300" : "text-blue-700"}`}>
      {text}
    </span>
  );
}
function Divider({ light = false }: { light?: boolean }) {
  return <div className={`w-11 h-[3px] mb-6 ${light ? "bg-blue-400" : "bg-blue-700"}`} />;
}

/* ─── Data ────────────────────────────────────────────────── */
const LOAN_TYPES = [
  { type: "Term Loans",               best: "Business expansion, equipment, infrastructure",        feature: "Long-term repayment up to 10 years" },
  { type: "Working Capital Loans",    best: "Day-to-day operations, inventory, payroll",            feature: "Short-term, revolving credit options" },
  { type: "MSME Priority Lending",    best: "MSME-registered businesses",                           feature: "Preferential interest rates + government support" },
  { type: "Collateral-Free Loans",    best: "Businesses without fixed assets",                      feature: "CGTMSE-covered, no security required" },
  { type: "Project Financing",        best: "Large-scale capital projects",                         feature: "Customized structure for complex financing" },
  { type: "Government Scheme Loans",  best: "Eligible entrepreneurs & startups",                    feature: "MUDRA, Stand-Up India, CGTMSE access" },
  { type: "Startup Financing",        best: "Early-stage and growth-stage startups",                feature: "Flexible terms, lower documentation bar" },
  { type: "Business Expansion Loans", best: "Scaling into new markets",                             feature: "Higher loan amounts, longer tenures" },
];

const ELIGIBILITY = [
  "Minimum 3 years of business vintage (varies by lender and loan type)",
  "Annual turnover meeting lender-specified threshold for loan amount",
  "CIBIL score of 650 or above (higher scores attract better rates)",
  "Positive cash flow demonstrated over 2+ years",
  "Valid business registration (Pvt Ltd, LLP, Partnership, Proprietorship)",
  "PAN and TAN of the business and promoters",
  "GST registration and regular filing (if applicable)",
  "ITR filings for the previous 2–3 years",
  "Collateral or security if required by the lender (not required for CGTMSE-covered loans)",
];

const STEPS = [
  { n: "01", title: "Requirement Analysis",    body: "We understand your business needs, loan amount and financing objectives to define the right brief." },
  { n: "02", title: "Eligibility Assessment",  body: "We evaluate your financials, credit profile and documentation readiness against lender criteria." },
  { n: "03", title: "Loan Structuring",        body: "We design the optimal loan amount, tenure, moratorium and repayment plan for your cash flow." },
  { n: "04", title: "Lender Matching",         body: "We submit applications to the most suitable lenders from our network for your profile and requirements." },
  { n: "05", title: "Term Negotiation",        body: "We negotiate interest rates, processing fees and conditions on your behalf to secure the best terms." },
  { n: "06", title: "Disbursement Facilitation", body: "We assist with final documentation and ensure smooth fund release to your business account." },
];

const DOCUMENTS = [
  "Business registration certificate (Pvt Ltd / LLP / Partnership / GST certificate)",
  "Memorandum and Articles of Association (for companies)",
  "GST registration certificate and last 12 months of GSTR returns",
  "Audited financial statements for the past 2–3 financial years",
  "Income Tax Returns of the business and promoters (2–3 years)",
  "Bank statements of the business account (last 12 months)",
  "KYC documents: PAN, Aadhaar, passport-size photographs of all directors/partners",
  "Proof of business premises: owned (sale deed) or rented (rent agreement + NOC)",
  "Business profile or project report (for project/expansion loans)",
  "Existing loan statements and sanction letters (if applicable)",
  "Collateral documents: property papers, valuation report (if applicable)",
];

const FAQS = [
  { q: "What is the minimum loan amount you facilitate?",  a: "We facilitate loans starting from ₹5 lakh up to ₹50 crore depending on the business profile and lender. The right loan amount is determined during your eligibility assessment based on your turnover, cash flow and financing objective." },
  { q: "How long does loan processing take?",              a: "Typically 7–21 working days from complete document submission, depending on the lender and loan type. Government scheme loans and MSME priority lending may follow separate timelines. We keep you updated at every stage of the process." },
  { q: "Do you charge for loan facilitation?",             a: "We charge a nominal facilitation fee only upon successful loan disbursement. There are no upfront charges, no consultation fees and no charges if the loan is not sanctioned. Our fee is fully aligned with your success." },
  { q: "Can I get a loan if my CIBIL score is low?",       a: "Yes — some NBFC lenders and government schemes accept lower CIBIL scores and certain collateral-backed products have less stringent credit requirements. We assess your full financial profile and identify the best available options rather than applying a single cutoff." },
  { q: "What is a collateral-free loan and how does CGTMSE work?", a: "Under the CGTMSE (Credit Guarantee Fund Trust for Micro and Small Enterprises) scheme, the government provides a guarantee to participating lenders — allowing MSMEs to access loans without pledging any fixed assets or property. Eligible businesses can access up to ₹5 crore collateral-free under this scheme." },
  { q: "Can startups and early-stage businesses apply?",   a: "Yes — we facilitate startup financing with lenders who offer flexible terms and lower documentation bars for early-stage businesses. Government schemes such as MUDRA and Stand-Up India are also accessible to new businesses. We advise on the most viable options based on your stage and financials." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-[22px]">
          <p className="text-[14px] text-slate-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Loan Type Row ───────────────────────────────────────── */
function LoanRow({ row, i }: { row: { type: string; best: string; feature: string }; i: number }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`grid border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"} ${i === 0 ? "border-t" : ""}`}
      style={{ transitionDelay: `${i * 0.04}s`, gridTemplateColumns: "200px 1fr 220px" }}
    >
      <div className="py-4 px-5 bg-slate-50 border-r border-slate-200 flex items-center">
        <span className="text-[12px] font-bold text-slate-700 tracking-[0.05em] uppercase leading-snug">{row.type}</span>
      </div>
      <div className="py-4 px-5 border-r border-slate-200 flex items-center">
        <span className="text-[13.5px] text-slate-500 leading-relaxed">{row.best}</span>
      </div>
      <div className="py-4 px-5 flex items-center">
        <span className="text-[13px] font-medium text-blue-700 leading-snug">{row.feature}</span>
      </div>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function BusinessLoansPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-[30px] bg-white">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <Link href="/services/funding" className="text-blue-700 font-medium hover:underline">Funding Assistance</Link>
            <span className="text-slate-300">›</span>
            <span>Business Loans</span>
          </nav>

          <div className="flex flex-wrap gap-2 mb-5">
            {["Term Loans", "Working Capital", "MSME Schemes", "Collateral-Free"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">{tag}</span>
            ))}
          </div>

          <Eyebrow text="Funding Assistance — Loans" />
          <Divider />

          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-slate-900 leading-[1.1] mb-5 ">
            Business Loan Solutions Tailored to Every Growth Stage
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed mb-9 ">
            From working capital to large-scale expansion — we match your business with the right loan product, at the best terms, with complete documentation support from application to disbursement.
          </p>
          <div className="flex gap-3.5">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
              CHECK LOAN ELIGIBILITY
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              TALK TO A SPECIALIST
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=900&q=85"
            alt="Business loan financing"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "8",       l: "Loan Types Facilitated" },
          { v: "₹5L–50Cr",l: "Loan Range" },
          { v: "7–21",    l: "Working Days to Disburse" },
          { v: "Free",    l: "Eligibility Assessment" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-base sm:text-lg lg:text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ LOAN TYPES TABLE ═══════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-12">
              <Eyebrow text="Loan Types We Facilitate" />
              <Divider />
              <div className="flex justify-between items-end flex-wrap gap-4">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight m-0">
                  Eight Loan Products.<br />Every Business Need Covered.
                </h2>
                <p className="text-[14px] text-slate-500 max-w-[360px] leading-relaxed">
                  From collateral-free MSME lending to large project financing — we identify and facilitate the right product for your profile.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-x-auto">
            {/* Table header */}
            <div
              className="grid border-b-2 border-blue-700 mb-0"
              style={{ gridTemplateColumns: "200px 1fr 220px" }}
            >
              {["Loan Type", "Best For", "Key Feature"].map((h, i) => (
                <div key={i} className={`py-3 px-5 ${i < 2 ? "border-r border-slate-200" : ""}`}>
                  <span className="text-[11px] font-bold text-blue-700 tracking-[0.12em] uppercase">{h}</span>
                </div>
              ))}
            </div>
            {LOAN_TYPES.map((row, i) => <LoanRow key={i} row={row} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ ELIGIBILITY + PROCESS ══════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-900">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Eligibility Requirements" light />
            <Divider light />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-white leading-tight mb-5">
              Standard Eligibility Criteria
            </h2>
            <p className="text-[15px] font-light text-slate-300 leading-relaxed mb-8">
              These are the typical criteria across most lenders. Requirements vary by loan type, lender and business structure — we advise on your exact position during the free eligibility assessment.
            </p>
            <div className="flex flex-col gap-3.5">
              {ELIGIBILITY.map((item, i) => (
                <div key={i} className="flex items-start gap-3.5">
                  <div className="w-5 h-5 bg-blue-600 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-[14px] text-slate-300 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <Eyebrow text="Our 6-Step Process" light />
            <Divider light />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-white leading-tight mb-8">
              Loan Facilitation Process
            </h2>
            <div>
              {STEPS.map((s, i) => (
                <div
                  key={i}
                  className={`flex gap-4 items-start relative ${i < STEPS.length - 1 ? "pb-7" : ""}`}
                >
                  {i < STEPS.length - 1 && (
                    <div className="absolute left-[17px] top-9 w-px bottom-0 bg-slate-700" />
                  )}
                  <div className="w-9 h-9 bg-blue-600 flex-shrink-0 flex items-center justify-center">
                    <span className="text-[11px] font-bold text-white">{s.n}</span>
                  </div>
                  <div className="pt-1.5">
                    <p className="text-[14px] font-bold text-white mb-1.5">{s.title}</p>
                    <p className="text-[13.5px] text-slate-400 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ DOCUMENTATION ══════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Required Documentation" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Common Documents for All Loan Types
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              Documentation requirements vary by loan type, lender and business structure. We advise you on the exact list during consultation — and assist with preparation and formatting where needed.
            </p>
            <div className="flex flex-col gap-3">
              {DOCUMENTS.map((doc, i) => (
                <div key={i} className="flex items-start gap-3.5">
                  <div className="w-5 h-5 bg-blue-700 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-[13.5px] text-slate-500 leading-relaxed">{doc}</span>
                </div>
              ))}
            </div>
            <div className="mt-7 border-l-2 border-blue-700 pl-4">
              <p className="text-[13px] text-slate-500 leading-relaxed italic">
                Note: Documentation requirements vary by loan type, lender and business structure. We advise you on the exact list during consultation.
              </p>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <img
              src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=700&q=80"
              alt="Loan documentation and paperwork"
              className="w-full h-[200px] sm:h-[280px] lg:h-[340px] object-cover mb-8"
            />
            <div className="bg-slate-50 border border-slate-200 p-7">
              <p className="text-[13px] font-bold text-blue-700 tracking-[0.1em] uppercase mb-3">We Handle the Paperwork</p>
              <p className="text-[14px] text-slate-500 leading-relaxed mb-4">
                Our team reviews your documents before submission, identifies gaps and works with you to prepare a complete, lender-ready application file — reducing rejection risk and processing time.
              </p>
              <div className="flex flex-col gap-2.5">
                {[
                  "Pre-submission document review and gap analysis",
                  "Formatting and bundling for lender requirements",
                  "Project report preparation for expansion and project loans",
                  "Liaison with lender relationship managers on your behalf",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-blue-700 flex-shrink-0 mt-2 rounded-full" />
                    <span className="text-[13px] text-slate-500 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About Business Loans
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Clear answers to the questions we hear most from business owners exploring financing for the first time or looking to refinance existing debt.
            </p>
            <img
              src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=700&q=80"
              alt="Business loan consultation"
              className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <video
              src="/videos/CTA.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[220px] sm:h-[300px] lg:h-[380px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Ready to Secure Business Financing?" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Connect with Our Loan Specialists Today
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              Free eligibility assessment, no upfront charges, expert negotiation on your behalf — from application to disbursement.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Most businesses discover better loan options than they initially approached — the right lender match makes a significant difference in rate, tenure and approval probability.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
                CHECK LOAN ELIGIBILITY
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                TALK TO A SPECIALIST
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}